import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface } from "@/components/shared";
import { formatDate, formatDateTime } from "@/lib/datetime";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Search, ArrowDownCircle, ArrowUpCircle, Package, TrendingDown, TrendingUp, X, ChevronLeft, ChevronRight, History, Layers2, Download, Printer } from "lucide-react";
import ComboBoxSearch from "@/components/ComboBoxSearch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const STOCK_PAGE_SIZE = 250;

const TYPE_COLOR = {
  "Main Raw Material": "bg-blue-50 text-blue-700",
  "Stones": "bg-purple-50 text-purple-700",
  "Paints": "bg-amber-50 text-amber-700",
  "Decorative": "bg-emerald-50 text-emerald-700",
  "Others": "bg-gray-100 text-gray-700",
};

export default function RMStocks() {
  return (
    <div className="space-y-3">
      <CurrentStock />
    </div>
  );
}

// ---------- Current Stock -----------------------------------------------------
function CurrentStock() {
  const [items, setItems] = useState([]);
  const [historyFor, setHistoryFor] = useState(null);
  const [totals, setTotals] = useState({ total_materials: 0, in_stock: 0, out_of_stock: 0, negative: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // "Pending" = what the user is typing / picking. "Committed" = what actually
  // drives the API call. We only push pending → committed when the user hits
  // Search, so RMs no longer auto-load on every keystroke.
  const [pSearch, setPSearch] = useState("");
  const [pType, setPType] = useState("all");
  const [pCategory, setPCategory] = useState("all");
  const [pStatus, setPStatus] = useState("all");
  const [committed, setCommitted] = useState(null);   // null = nothing searched yet
  const [enums, setEnums] = useState({ rm_types: [] });
  const [categories, setCategories] = useState([]);
  // Group View — selected rows (Map<id, row>) so we can compare picks that
  // may have scrolled off the current page.
  const [selected, setSelected] = useState(() => new Map());
  const [groupOpen, setGroupOpen] = useState(false);
  const toggleSelect = (row) => setSelected((prev) => {
    const next = new Map(prev);
    if (next.has(row.id)) next.delete(row.id); else next.set(row.id, row);
    return next;
  });

  useEffect(() => {
    api.get("/production/enums").then((r) => setEnums(r.data));
    api.get("/categories").then((r) => setCategories(r.data || []));
  }, []);

  const runSearch = () => {
    setPage(1);
    setCommitted({ search: pSearch, type: pType, category: pCategory, stockStatus: pStatus });
  };
  const resetFilters = () => {
    setPSearch(""); setPType("all"); setPCategory("all"); setPStatus("all");
    setCommitted(null);
    setItems([]); setTotal(0);
    setTotals({ total_materials: 0, in_stock: 0, out_of_stock: 0, negative: 0 });
  };

  const load = useCallback(async () => {
    if (!committed) return;
    setLoading(true);
    try {
      const params = { page, page_size: STOCK_PAGE_SIZE };
      if (committed.search) params.search = committed.search;
      if (committed.type !== "all") params.type = committed.type;
      if (committed.category !== "all") params.category = committed.category;
      if (committed.stockStatus !== "all") params.stock_status = committed.stockStatus;
      const r = await api.get("/production/rm-stocks", { params });
      setItems(r.data.items);
      setTotals(r.data.totals);
      setTotal(r.data.total ?? (r.data.items || []).length);
    } finally {
      setLoading(false);
    }
  }, [committed, page]);

  useEffect(() => { if (committed) load(); }, [committed, page, load]);

  const totalPages = Math.max(1, Math.ceil(total / STOCK_PAGE_SIZE));

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-4 gap-3">
        <KpiTile label="Total Materials" value={totals.total_materials} icon={Package} tone="gray" />
        <KpiTile label="In Stock" value={totals.in_stock} icon={TrendingUp} tone="emerald" />
        <KpiTile label="Out of Stock" value={totals.out_of_stock} icon={TrendingDown} tone="amber" />
        <KpiTile label="Negative" value={totals.negative} icon={TrendingDown} tone="red" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input
            placeholder="Search raw materials…"
            className="pl-8 h-9 text-sm bg-white"
            value={pSearch}
            onChange={(e) => setPSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runSearch()}
            data-testid="stk-search"
          />
        </div>
        <Select value={pType} onValueChange={setPType}>
          <SelectTrigger className="w-52 h-9 text-sm" data-testid="stk-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {(enums.rm_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={pStatus} onValueChange={setPStatus}>
          <SelectTrigger className="w-44 h-9 text-sm" data-testid="stk-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Stock Status</SelectItem>
            <SelectItem value="in_stock">In Stock (&gt;0)</SelectItem>
            <SelectItem value="out">Out of Stock (=0)</SelectItem>
            <SelectItem value="negative">Negative (&lt;0)</SelectItem>
          </SelectContent>
        </Select>
        <div className="w-56">
          <ComboBoxSearch
            value={pCategory === "all" ? null : pCategory}
            onChange={(v) => setPCategory(v || "all")}
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            placeholder="All Categories"
            clearable
            testId="stk-category-filter"
          />
        </div>
        <Button size="sm" className="h-9 gap-1.5 bg-[#9B1C1C] hover:bg-[#7F1D1D]" onClick={runSearch} data-testid="stk-search-btn">
          <Search className="h-4 w-4" /> Search
        </Button>
        <Button size="sm" variant="outline" className="h-9" onClick={resetFilters} data-testid="stk-reset-btn">Reset</Button>
        {selected.size > 0 && (
          <Button size="sm" className="h-9 gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => setGroupOpen(true)} data-testid="stk-group-view-btn">
            <Layers2 className="h-4 w-4" /> Group View ({selected.size})
          </Button>
        )}
        <span className="ml-auto text-xs text-gray-500 tabular-nums" data-testid="stk-total-count">
          {committed ? `${total.toLocaleString()} row${total !== 1 ? "s" : ""}` : "Apply filters and click Search"}
        </span>
      </div>

      <Surface padded={false}>
        <div className="overflow-auto max-h-[calc(100vh-370px)]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-2 py-2.5 w-8"><span className="sr-only">Select</span></th>
                <th className="px-3 py-2.5">Raw Material</th>
                <th className="px-3 py-2.5">Type</th>
                <th className="px-3 py-2.5">Unit</th>
                <th className="px-3 py-2.5">Vendor</th>
                <th className="px-3 py-2.5 text-right">Received (IN)</th>
                <th className="px-3 py-2.5 text-right">Consumed (OUT)</th>
                <th className="px-3 py-2.5 text-right">Current Stock</th>
                <th className="px-3 py-2.5">Last Movement</th>
                <th className="px-3 py-2.5 text-center">History</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={10} className="text-center py-8 text-gray-400">Loading…</td></tr>}
              {!loading && !committed && (
                <tr><td colSpan={10} className="text-center py-16 text-gray-500">
                  <Package className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Set your filters above, then click <span className="font-semibold text-[#9B1C1C]">Search</span> to load matching raw materials.</p>
                  <p className="text-xs text-gray-400 mt-1">Nothing loads until you search — keeps the page snappy with tens of thousands of RMs.</p>
                </td></tr>
              )}
              {!loading && committed && items.length === 0 && (
                <tr><td colSpan={10} className="text-center py-12 text-gray-400">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  No matching raw materials
                </td></tr>
              )}
              {items.map((r) => (
                <tr key={r.id} className={`hover:bg-gray-50 ${selected.has(r.id) ? "bg-emerald-50/40" : ""}`} data-testid={`stk-row-${r.id}`}>
                  <td className="px-2 py-2 text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleSelect(r)}
                      className="h-4 w-4 accent-emerald-700 cursor-pointer"
                      aria-label={`Select ${r.name} for Group View`}
                      data-testid={`stk-select-${r.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-900">{r.name}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${TYPE_COLOR[r.type] || TYPE_COLOR.Others}`}>
                      {r.type}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{r.unit}</td>
                  <td className="px-3 py-2" data-testid={`stk-vendor-${r.id}`}>
                    {r.vendor_name && r.vendor_name.toLowerCase() !== "nil" ? (
                      <span className="inline-block bg-slate-50 border border-slate-200 text-slate-800 px-1.5 py-0.5 rounded text-[11px] leading-tight">
                        {r.vendor_name}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">Unassigned</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-emerald-700 tabular-nums">{fmtQty(r.total_in)}</td>
                  <td className="px-3 py-2 text-right text-amber-700 tabular-nums">{fmtQty(r.total_out)}</td>
                  <td className={`px-3 py-2 text-right font-bold tabular-nums ${
                    r.current_stock < 0 ? "text-red-700" : r.current_stock === 0 ? "text-gray-400" : "text-gray-900"
                  }`}>{fmtQty(r.current_stock)}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{fmtDate(r.last_movement_at)}</td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => setHistoryFor(r)}
                      className="h-7 px-2 text-xs rounded bg-gray-50 hover:bg-gray-100 text-gray-700 font-medium border border-gray-200 inline-flex items-center gap-1"
                      data-testid={`stk-history-${r.id}`}
                    >
                      <History className="h-3 w-3" /> History
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Surface>

      {total > STOCK_PAGE_SIZE && (
        <div className="flex items-center justify-between text-xs text-gray-600" data-testid="stk-pagination">
          <span>
            Showing {((page - 1) * STOCK_PAGE_SIZE) + 1}–{Math.min(page * STOCK_PAGE_SIZE, total)} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="stk-page-prev" className="h-8 gap-1">
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </Button>
            <span className="tabular-nums font-medium text-gray-700" data-testid="stk-page-info">
              Page {page} of {totalPages}
            </span>
            <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => setPage((p) => p + 1)} data-testid="stk-page-next" className="h-8 gap-1">
              Next <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
      {historyFor && <RMHistoryModal rm={historyFor} onClose={() => setHistoryFor(null)} />}
      {groupOpen && (
        <RMGroupViewDialog
          rows={Array.from(selected.values())}
          onClose={() => { setGroupOpen(false); setSelected(new Map()); }}
        />
      )}
    </div>
  );
}

function KpiTile({ label, value, icon: Icon, tone }) {
  const tones = {
    gray: "bg-white border-gray-200 text-gray-900",
    emerald: "bg-emerald-50 border-emerald-100 text-emerald-800",
    amber: "bg-amber-50 border-amber-100 text-amber-800",
    red: "bg-red-50 border-red-100 text-red-800",
  };
  return (
    <div className={`rounded-md border p-3 ${tones[tone]}`}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-widest opacity-70">{label}</p>
        <Icon className="h-4 w-4 opacity-60" />
      </div>
      <p className="mt-1 text-2xl font-heading font-bold tabular-nums">{value.toLocaleString()}</p>
    </div>
  );
}

// ---------- Movement Ledger --------------------------------------------------
function MovementLedger() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [materials, setMaterials] = useState([]);

  const [rmId, setRmId] = useState(null);
  const [direction, setDirection] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => {
    api.get("/production/raw-materials").then((r) => setMaterials(r.data || []));
  }, []);

  useEffect(() => { setPage(1); }, [rmId, direction, dateFrom, dateTo]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (rmId) params.raw_material_id = rmId;
      if (direction && direction !== "all") params.direction = direction;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const r = await api.get("/production/rm-ledger", { params });
      setItems(r.data.items);
      setTotal(r.data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [rmId, direction, dateFrom, dateTo, page]);

  const clearFilters = () => {
    setRmId(null); setDirection("all"); setDateFrom(""); setDateTo("");
  };

  const activeCount = (rmId ? 1 : 0) + (direction !== "all" ? 1 : 0) + (dateFrom ? 1 : 0) + (dateTo ? 1 : 0);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <ComboBoxSearch
          value={rmId}
          onChange={setRmId}
          options={materials.map((m) => ({ value: m.id, label: m.name, hint: m.unit }))}
          placeholder="All Raw Materials"
          testId="ldg-material"
          clearable
          className="w-64"
        />
        <Select value={direction} onValueChange={setDirection}>
          <SelectTrigger className="w-36 h-9 text-sm" data-testid="ldg-direction"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Directions</SelectItem>
            <SelectItem value="IN">IN (Received)</SelectItem>
            <SelectItem value="OUT">OUT (Consumed)</SelectItem>
          </SelectContent>
        </Select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 rounded-md border border-gray-200 px-2.5 text-sm" placeholder="From" data-testid="ldg-from" />
        <span className="text-xs text-gray-400">→</span>
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 rounded-md border border-gray-200 px-2.5 text-sm" placeholder="To" data-testid="ldg-to" />
        {activeCount > 0 && (
          <button onClick={clearFilters} className="h-9 px-2 text-xs font-semibold text-[#9B1C1C] hover:underline flex items-center gap-1">
            <X className="h-3 w-3" /> Clear
          </button>
        )}
        <span className="ml-auto text-xs text-gray-500">{total.toLocaleString()} movement{total !== 1 ? "s" : ""}</span>
      </div>

      <Surface padded={false}>
        <div className="overflow-auto max-h-[calc(100vh-260px)]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2.5">Date & Time</th>
                <th className="px-3 py-2.5">Direction</th>
                <th className="px-3 py-2.5">Raw Material</th>
                <th className="px-3 py-2.5 text-right">Qty</th>
                <th className="px-3 py-2.5">Source</th>
                <th className="px-3 py-2.5">Reference</th>
                <th className="px-3 py-2.5">Vendor / Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">
                  <Package className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  No stock movements yet
                </td></tr>
              )}
              {items.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50" data-testid={`ldg-row-${m.id}`}>
                  <td className="px-3 py-2 text-gray-600 text-xs whitespace-nowrap">{fmtDateTime(m.created_at)}</td>
                  <td className="px-3 py-2">
                    {m.direction === "IN" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px] font-semibold">
                        <ArrowDownCircle className="h-3 w-3" /> IN
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-[11px] font-semibold">
                        <ArrowUpCircle className="h-3 w-3" /> OUT
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-medium text-gray-900">{m.raw_material_name}</td>
                  <td className={`px-3 py-2 text-right font-semibold tabular-nums ${m.direction === "IN" ? "text-emerald-700" : "text-amber-700"}`}>
                    {m.direction === "IN" ? "+" : "−"}{fmtQty(m.qty)} {m.unit}
                  </td>
                  <td className="px-3 py-2 text-gray-600 text-xs">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${
                      m.source_type === "purchase" ? "bg-blue-50 text-blue-700" :
                      m.source_type === "purchase_reversal" ? "bg-red-50 text-red-700" :
                      "bg-gray-100 text-gray-700"
                    }`}>{m.source_type}</span>
                  </td>
                  <td className="px-3 py-2 text-gray-600 font-mono text-xs">{m.purchase_no || m.source_ref || "—"}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{m.vendor_name || m.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Surface>

      {total > pageSize && (
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>Page {page} of {Math.max(1, Math.ceil(total / pageSize))}</span>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</Button>
            <Button size="sm" variant="outline" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtQty(n) {
  if (n === null || n === undefined) return "—";
  const v = Math.round(n * 100) / 100;
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function fmtDate(iso) { return formatDate(iso) || "—"; }
function fmtDateTime(iso) { return formatDateTime(iso) || "—"; }


function RMHistoryModal({ rm, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/production/rm-ledger", { params: { raw_material_id: rm.id, page: 1, page_size: 500 } })
      .then((r) => setRows((r.data?.items || []).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [rm.id]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="rm-history-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-gray-200 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#9B1C1C]/10 border border-[#9B1C1C]/20 flex items-center justify-center">
            <History className="h-5 w-5 text-[#9B1C1C]" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg text-gray-900">{rm.name}</h3>
            <p className="text-xs text-gray-500">
              {rm.code || ""}{rm.unit ? ` · ${rm.unit}` : ""} · Current stock: <b className="text-gray-900 tabular-nums">{rm.current_stock ?? 0}</b> · {rows.length} movements
            </p>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded hover:bg-gray-100 inline-flex items-center justify-center" data-testid="rm-history-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2 w-44">Date &amp; Time</th>
                <th className="px-3 py-2 text-right w-24">In Qty</th>
                <th className="px-3 py-2 text-right w-24">Out Qty</th>
                <th className="px-3 py-2 text-right w-28">Balance</th>
                <th className="px-3 py-2 w-36">Source</th>
                <th className="px-3 py-2 w-36">Reference</th>
                <th className="px-3 py-2">Vendor / Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">No movements yet.</td></tr>}
              {rows.map((r) => {
                const isIn = r.direction === "IN";
                return (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-xs tabular-nums">{fmtDateTime(r.created_at)}</td>
                    <td className={`px-3 py-2 text-right tabular-nums text-xs font-semibold ${isIn ? "text-emerald-700" : "text-gray-300"}`}>{isIn ? r.qty : "—"}</td>
                    <td className={`px-3 py-2 text-right tabular-nums text-xs font-semibold ${!isIn ? "text-red-700" : "text-gray-300"}`}>{!isIn ? r.qty : "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-xs font-semibold text-gray-900" data-testid={`rm-hist-balance-${r.id}`}>
                      {r.balance_after != null ? Number(r.balance_after).toLocaleString(undefined, { maximumFractionDigits: 3 }) : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-semibold bg-gray-100 text-gray-700 border border-gray-200">{(r.source_type || "—").replace(/_/g, " ")}</span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono font-semibold text-[#9B1C1C]">{r.source_ref || "—"}</td>
                    <td className="px-3 py-2 text-xs text-gray-600">{r.vendor_name || r.notes || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RM Group View — side-by-side comparison of hand-picked raw materials.
// Rows come straight from what the user had on screen — no extra fetch — and
// we compare type, unit, vendor, received, consumed, current stock and last
// movement.
// ─────────────────────────────────────────────────────────────────────────────
function RMGroupViewDialog({ rows, onClose }) {
  const fmtQty = (v) => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 3 }));
  const fmtWhen = (v) => (v ? formatDateTime(v) : "—");

  const printCompare = () => {
    const doc = window.open("", "_blank", "noopener,noreferrer,width=1200,height=800");
    if (!doc) { toast.error("Pop-up blocked — enable pop-ups for this site"); return; }
    const body = rows.map((r) => `<tr>
      <td>${escapeHtml(r.name || "")}</td>
      <td>${escapeHtml(r.type || "")}</td>
      <td>${escapeHtml(r.unit || "")}</td>
      <td>${escapeHtml(r.vendor_name || "—")}</td>
      <td class="num">${fmtQty(r.total_in)}</td>
      <td class="num">${fmtQty(r.total_out)}</td>
      <td class="num total">${fmtQty(r.current_stock)}</td>
      <td>${fmtWhen(r.last_movement_at)}</td>
    </tr>`).join("");
    doc.document.write(`<!doctype html><html><head><title>RM Stock Comparison — Thara Gold</title>
      <style>
        body { font-family: -apple-system,BlinkMacSystemFont,sans-serif; padding: 24px; color: #111; }
        h1 { font-size: 18px; margin: 0 0 4px; }
        p.sub { color: #666; font-size: 12px; margin: 0 0 16px; }
        table { border-collapse: collapse; width: 100%; font-size: 12px; }
        th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
        th { background: #f5f5f5; }
        td.num { text-align: right; font-variant-numeric: tabular-nums; }
        td.total { background: #fef7ea; font-weight: 600; }
        @media print { body { padding: 12px; } }
      </style></head><body>
      <h1>RM Stock Comparison — ${rows.length} raw material${rows.length !== 1 ? "s" : ""}</h1>
      <p class="sub">Generated ${new Date().toLocaleString()}</p>
      <table><thead><tr>
        <th>Raw Material</th><th>Type</th><th>Unit</th><th>Vendor</th>
        <th class="num">Received</th><th class="num">Consumed</th><th class="num">Current</th><th>Last Movement</th>
      </tr></thead><tbody>${body}</tbody></table>
    </body></html>`);
    doc.document.close();
    doc.focus();
    setTimeout(() => doc.print(), 250);
  };

  const exportCsv = () => {
    const header = ["Raw Material", "Type", "Unit", "Vendor", "Received (IN)", "Consumed (OUT)", "Current Stock", "Last Movement"];
    const body = rows.map((r) => [
      r.name || "", r.type || "", r.unit || "", r.vendor_name || "",
      r.total_in ?? 0, r.total_out ?? 0, r.current_stock ?? 0, r.last_movement_at || "",
    ]);
    const csv = [header, ...body].map((cols) => cols.map(csvEscape).join(",")).join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rm-stock-comparison-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[95vw] w-[1300px] max-h-[92vh] overflow-hidden flex flex-col p-0" data-testid="rm-group-view-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b">
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <DialogTitle className="flex items-center gap-2">
                <Layers2 className="h-5 w-5 text-emerald-700" /> RM Stock Comparison
              </DialogTitle>
              <DialogDescription>
                Side-by-side view of <b>{rows.length}</b> selected raw material{rows.length !== 1 ? "s" : ""}.
              </DialogDescription>
            </div>
            <Button variant="outline" size="sm" onClick={exportCsv} disabled={!rows.length} className="gap-1.5" data-testid="rm-group-view-export-csv">
              <Download className="h-4 w-4" /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={printCompare} disabled={!rows.length} className="gap-1.5" data-testid="rm-group-view-print">
              <Printer className="h-4 w-4" /> Print
            </Button>
          </div>
        </DialogHeader>

        <div className="overflow-auto flex-1 px-6 py-4">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="border-b-2 border-gray-200 text-left">
                <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-gray-500">Raw Material</th>
                <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-gray-500">Type</th>
                <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-gray-500">Unit</th>
                <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-gray-500">Vendor</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase tracking-widest text-emerald-700">Received (IN)</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase tracking-widest text-amber-700">Consumed (OUT)</th>
                <th className="px-3 py-2 text-right text-[11px] uppercase tracking-widest text-amber-800 bg-amber-50/50">Current</th>
                <th className="px-3 py-2 text-[11px] uppercase tracking-widest text-gray-500">Last Movement</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50" data-testid={`rm-group-view-row-${r.id}`}>
                  <td className="px-3 py-2.5 font-medium text-gray-900">{r.name}</td>
                  <td className="px-3 py-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${TYPE_COLOR[r.type] || TYPE_COLOR.Others}`}>{r.type}</span>
                  </td>
                  <td className="px-3 py-2.5 text-gray-700 text-xs">{r.unit}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-700">
                    {r.vendor_name && r.vendor_name.toLowerCase() !== "nil" ? r.vendor_name : <span className="text-gray-400">Unassigned</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-emerald-700">{fmtQty(r.total_in)}</td>
                  <td className="px-3 py-2.5 text-right font-mono tabular-nums text-amber-700">{fmtQty(r.total_out)}</td>
                  <td className={`px-3 py-2.5 text-right font-mono tabular-nums font-semibold bg-amber-50/40 ${
                    r.current_stock < 0 ? "text-red-700" : r.current_stock === 0 ? "text-gray-400" : "text-amber-800"
                  }`}>{fmtQty(r.current_stock)}</td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">{fmtWhen(r.last_movement_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <DialogFooter className="px-6 py-3 border-t bg-gray-50">
          <span className="text-xs text-gray-500 mr-auto">Close will clear your current selection.</span>
          <Button variant="outline" onClick={onClose} data-testid="rm-group-view-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function csvEscape(v) {
  const s = String(v ?? "");
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function escapeHtml(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

